import { Buffer } from 'buffer';
import * as Sentry from "@sentry/nuxt";

export const useBookingStore = defineStore('booking', {
  state: () => {
    return {
      moveReservation: null,
      rebookReservation: null,
      waitlist: null,
      serviceSelections: {},
      selectedSlot: null,
      selectedCustomer: null,
      selectedDate: null,
      mode: 'standard', // options standard, timetable, lastminute
      submission: null,
      voucher: null,
      pass: null,
      giftCard: null,
      guid: 'booking_' + (new Date()).getTime(),
      payment: {},
      filters: {}
    }
  },
  actions: {
    // Helper method to find the best applicable association for a service
    // Returns both the association and the parent service that provides it
    findApplicableAssociation(targetService: any) {
      let bestAssociation: any = null
      let parentService: any = null
      let highestDiscount = -1

      // Loop through all selected services
      for (const id in this.serviceSelections) {
        const selection = (this.serviceSelections as any)[id]

        if (!selection || !selection.selected || !selection.service.associations) {
          continue
        }

        // Skip services that were added as add-ons of the target service.
        // This prevents an add-on from applying a reverse discount back to its parent.
        // e.g. If Service A is primary and Service B is its add-on, Service B should not
        // discount Service A even if Service B's add-on settings include Service A.
        if (selection.association && targetService.associations?.some(
          (a: any) => a.id === selection.association.id
        )) {
          continue
        }

        // Check if this selected service has associations pointing to the target service
        for (const association of selection.service.associations) {
          if (association.target.id === targetService.id) {
            // Keep track of the association with the highest discount
            if (association.discountAmount > highestDiscount) {
              highestDiscount = association.discountAmount
              bestAssociation = association
              parentService = selection.service
            }
          }
        }
      }

      return { association: bestAssociation, parentService }
    },
    // Helper method to shift orders of services at or after a given position
    // skipServiceId: optional service ID to skip (e.g., the service being inserted)
    shiftServiceOrders(fromPosition: number, shiftAmount: number = 1, skipServiceId: number | null = null) {
      for (const id in this.serviceSelections) {
        const selection = (this.serviceSelections as any)[id]

        if (!selection || !selection.selected) {
          continue
        }

        // Skip the service being inserted
        if (skipServiceId !== null && selection.service.id === skipServiceId) {
          continue
        }

        if (selection.order >= fromPosition) {
          this.setServiceSelection({
            service: selection.service,
            selected: undefined,
            employee: undefined,
            association: undefined,
            order: selection.order + shiftAmount,
            disabled: undefined
          })
        }
      }
    },
    // Helper method to calculate the correct order position based on association orderType
    // OrderType: 1 = BEFORE_SERVICE, 0 = AFTER_SERVICE
    calculateOrderPosition(parentService: any, orderType: number) {
      if (!parentService) {
        return this.maxOrder + 1
      }

      const parentSelection = (this.serviceSelections as any)[parentService.id]
      if (!parentSelection) {
        return this.maxOrder + 1
      }

      const parentOrder = parentSelection.order

      // OrderType 1 = BEFORE_SERVICE: insert before parent
      if (orderType === 1) {
        return parentOrder
      }

      // OrderType 0 = AFTER_SERVICE: insert after parent
      return parentOrder + 1
    },
    // Helper method to re-evaluate associations for all selected services
    // This updates the association field but doesn't reposition services
    // Repositioning happens automatically when services are added/removed
    // Also removes services that were added as add-ons if their parent is no longer selected
    reEvaluateAssociations() {
      const servicesToRemove: any[] = []

      for (const id in this.serviceSelections) {
        const selection = (this.serviceSelections as any)[id]

        if (!selection || !selection.selected) {
          continue
        }

        // Find the best association for this service
        const { association } = this.findApplicableAssociation(selection.service)

        // Only update if the association changed
        const currentAssociationId = selection.association?.id
        const newAssociationId = association?.id

        if (currentAssociationId !== newAssociationId) {
          // If this service had an association but no longer does (parent was removed),
          // mark it for removal
          if (currentAssociationId && !newAssociationId) {
            servicesToRemove.push(selection.service)
          } else {
            // Update the association (will be null if no association found)
            this.setServiceSelection({
              service: selection.service,
              selected: undefined,
              employee: undefined,
              association,
              order: undefined,
              disabled: undefined
            })
          }
        }
      }

      // Remove services that lost their parent association
      for (const service of servicesToRemove) {
        this.setServiceSelection({
          service,
          selected: false,
          employee: undefined,
          association: null,
          order: undefined,
          disabled: undefined
        })
      }

      // If we removed any services, reorder and disable
      if (servicesToRemove.length > 0) {
        this.reorder()
        this.disableServices()
      }
    },
    clear() {
      this.moveReservation = null
      this.rebookReservation = null
      this.waitlist = null
      this.serviceSelections = {}
      this.selectedSlot = null
      this.selectedCustomer = null
      this.selectedDate = null
      this.mode = 'standard'
      this.submission = null
      this.voucher = null
      this.pass = null
      this.giftCard = null
      this.guid = 'booking_' + (new Date()).getTime()
      this.payment = {}
      this.filters = {}
    },
    setServiceSelection({ service, selected, employee, association, idAssociation, order, disabled }) {
      let serviceSelection = this.serviceSelections[service.id]

      if (!serviceSelection) {
        serviceSelection = {}
        serviceSelection.service = service
        serviceSelection.selectedEmployee = null
        serviceSelection.selected = false
        serviceSelection.disabled = false
        serviceSelection.order = 0
        serviceSelection.association = null
        serviceSelection.idAssociation = null
      }

      if (selected !== undefined) serviceSelection.selected = selected
      if (employee !== undefined) serviceSelection.selectedEmployee = employee
      if (association !== undefined) serviceSelection.association = association
      if (idAssociation !== undefined) serviceSelection.idAssociation = idAssociation
      if (order !== undefined) serviceSelection.order = order
      if (disabled !== undefined) serviceSelection.disabled = disabled

      // Vue.set(this.serviceSelections, service.id, serviceSelection)

      this.serviceSelections[service.id] = serviceSelection
    },
    clearServiceSelections() {
      this.serviceSelections = {}
    },
    setSelectedDate(date) {
      this.selectedDate = date
    },
    setSelectedSlot({ slot, mode }) {
      this.selectedSlot = slot
      this.mode = mode
    },
    setSelectedCustomer(customer) {
      this.selectedCustomer = customer
    },
    setMode(mode) {
      this.mode = mode
    },
    setSubmission(submission) {
      this.submission = submission
    },
    setVoucher(voucher) {
      this.voucher = voucher
    },
    setPass(pass) {
      this.pass = pass
    },
    setGiftCard(giftCard) {
      this.giftCard = giftCard
    },
    setPayment({ guid, payment }) {
      if (payment) {
        this.payment[guid] = payment
      } else {
        delete this.payment[guid]
      }
    },
    setFilter({ filter, value }) {
      if (filter) {
        this.filters[filter] = value
      } else {
        delete this.filters[filter]
      }
    },
    setFilters(filters) {
      this.filters = filters
    },
    setMoveReservation({ moveReservation, includeEmployees = false }) {
      this.moveReservation = moveReservation

      const year = moveReservation.date.toString().substring(0, 4)
      const month = moveReservation.date.toString().substring(4, 6)
      const day = moveReservation.date.toString().substring(6, 8)

      this.setSelectedDate(new Date(year, month - 1, day))

      for (let i = 0; i < moveReservation.services.length; i++) {
        const reservationService = moveReservation.services[i]
        const employee = (includeEmployees) ? reservationService.employee : null

        this.setServiceSelected({
          service: reservationService.service,
          employee,
          association: reservationService.association,
          idAssociation: reservationService.idAssociation,
          selected: true,
          toBack: true
        })
      }

      if (moveReservation.voucher) {
        this.setVoucher(moveReservation.voucher)
      }

      if (moveReservation.pass) {
        this.setPass(moveReservation.pass)
      }
    },
    setRebookReservation(rebookReservation) {
      this.rebookReservation = rebookReservation

      this.setSelectedDate(new Date())

      for (let i = 0; i < rebookReservation.services.length; i++) {
        const reservationService = rebookReservation.services[i]

        this.setServiceSelected({
          service: reservationService.service,
          association: reservationService.association,
          idAssociation: reservationService.idAssociation,
          selected: true,
          toBack: true
        })
      }
    },
    setWaitlist({ waitlist, moveReservation = null } = {}) {
      this.waitlist = waitlist

      if (moveReservation) {
        this.moveReservation = moveReservation
      }
    },
    setServiceSelected({ service, selected, employee, association, idAssociation, toBack, reorderAndDisable }) {
      if (reorderAndDisable === undefined) reorderAndDisable = true
      if (toBack === undefined) {
        const selection = (this.serviceSelections as any)[service.id]
        if (!selection || !selection.selected) {
          toBack = true
        }
      }

      const locationStore = useLocationStore()
      const services = locationStore.services
      // get full version of service if available
      if (services) {
        for (let i = 0; i < services.categories.length; i++) {
          const category = services.categories[i]
          for (let j = 0; j < category.services.length; j++) {
            if (category.services[j].id == service.id) {
              service = category.services[j]
            }
          }
        }
      }

      // Auto-detect association if not explicitly provided and service is being selected
      let parentService = null
      if (association === undefined && selected !== false) {
        const result = this.findApplicableAssociation(service)
        association = result.association
        parentService = result.parentService
      }

      this.setServiceSelection({ service, selected, employee, association, idAssociation, order: undefined, disabled: undefined })

      // Determine the correct order position
      if (toBack) {
        let orderPosition = this.maxOrder + 1

        // If this service has an association with orderType, calculate position relative to parent
        if (association && parentService) {
          const orderType = association.orderType !== null ? association.orderType : 0
          orderPosition = this.calculateOrderPosition(parentService, orderType)

          // Shift existing services to make room for this service (skip the current service)
          this.shiftServiceOrders(orderPosition, 1, service.id)
        }

        this.setServiceSelection({ service, selected: undefined, employee: undefined, association: undefined, order: orderPosition, disabled: undefined })
      }

      if (services) {
        for (let i = 0; i < services.categories.length; i++) {
          const category = services.categories[i]
          for (let j = 0; j < category.services.length; j++) {
            if (category.services[j].id !== service.id) {
              this.setServiceSelection({ service: category.services[j], selected: undefined, employee: undefined, association: undefined, order: undefined, disabled: false })
            }
          }
        }
      }

      if (reorderAndDisable) {
        this.reorder()
        this.disableServices()
      }

      // Re-evaluate associations for all services after any change
      // This ensures that if a service is added/removed, all affected associations are updated
      this.reEvaluateAssociations()
    },
    reorder() {
      const serviceOrders = []

      for (const id in this.serviceSelections) {
        if (this.serviceSelections[id]) {
          const selection = this.serviceSelections[id]

          if (selection.selected) {
            const serviceInfo = {}
            serviceInfo.service = selection.service
            serviceInfo.order = selection.order

            serviceOrders.push(serviceInfo)
          }
        }
      }

      serviceOrders.sort(function (a, b) {
        return (a.order - b.order)
      })

      for (let i = 0; i < serviceOrders.length; i++) {
        this.setServiceSelection({ service: serviceOrders[i].service, order: (i + 1) })
      }
    },
    disableServices() {
      let hasGroupSelected = false
      let hasNonGroupSelected = false

      const locationStore = useLocationStore()
      const services = locationStore.getAllServices

      for (let i = 0; i < services.length; ++i) {
        const selection = this.serviceSelections[services[i].id]
        if (selection && selection.selected) {
          if (services[i].customerType === 1) {
            hasGroupSelected = true
          } else {
            hasNonGroupSelected = true
          }
        }
      }

      for (let i = 0; i < services.length; ++i) {
        const selection = this.serviceSelections[services[i].id]
        if (!selection || !selection.selected) {
          if (hasGroupSelected) {
            this.setServiceSelection({ service: services[i], disabled: true })
          } else if (hasNonGroupSelected && services[i].customerType === 1) {
            this.setServiceSelection({ service: services[i], disabled: true })
          } else {
            this.setServiceSelection({ service: services[i], disabled: false })
          }
        }
      }
    },
    async setFromUrlQuery(query = {}) {
      this.clear()

      const locationStore = useLocationStore()
      const { $api } = useNuxtApp()

      const currentQuery = this.urlQuery

      let servicesPromise = null
      let slotPromise = null
      let rebookPromise = null
      let movePromise = null
      let voucherPromise = null
      let passPromise = null

      if (((query.services && currentQuery.services !== query.services) || (query.slot && currentQuery.slot !== query.slot)) && !locationStore.services) {
        servicesPromise = locationStore.getServices()
      }

      if (!query.slot) {
        if (slotPromise) {
          this.setSelectedSlot({})
        }
      } else if (query.slot && currentQuery.slot !== query.slot) {
        slotPromise = $api.booking.slot(query.slot)
      }

      if (query.rebook && currentQuery.rebook !== query.rebook) {
        const rebookJson = Buffer.from(query.rebook, 'base64').toString()
        const rebookObject = JSON.parse(rebookJson)

        rebookPromise = $api.customer.reservation(rebookObject.id, rebookObject.date, { id: rebookObject.customer }, rebookObject.signature)
      }
      if (query.move && currentQuery.move !== query.move) {
        const moveJson = Buffer.from(query.move, 'base64').toString()
        const moveObject = JSON.parse(moveJson)

        movePromise = $api.customer.reservation(moveObject.id, moveObject.date, { id: moveObject.customer }, moveObject.signature)
      }

      if (query.voucher && currentQuery.voucher !== query.voucher) {
        voucherPromise = $api.booking.voucher(query.voucher).catch((error) => error)
      }

      if (query.pass && currentQuery.pass !== query.pass) {
        const passJson = Buffer.from(query.pass, 'base64').toString()
        const passObject = JSON.parse(passJson)

        passPromise = $api.booking.customerPass(passObject.id, passObject.signature)
      }

      await Promise.all([servicesPromise, slotPromise, rebookPromise, movePromise, voucherPromise, passPromise]).then((values) => {
        if (slotPromise) this.setSelectedSlot({ slot: values[1].slot })
        if (rebookPromise) this.rebookReservation = values[2].reservation
        if (movePromise) this.moveReservation = values[3].reservation
        if (voucherPromise) this.setVoucher(values[4]?.voucher)
        if (passPromise) this.setPass(values[5].customerPass)

        if (values[1] && values[1].slot) {
          for (let i = 0; i < values[1].slot.services.length; i++) {
            const slotService = values[1].slot.services[i]

            this.setServiceSelection({ service: slotService.service, employee: slotService.employee, selected: true, order: (i + 1), association: undefined, disabled: undefined })
          }

          this.disableServices()
        }
      }).catch((e: any) => {
        // console.log(e)
      })

      if (!query.services && !query.slot) {
        this.clearServiceSelections()
      } else if (query.services && currentQuery.services !== query.services) {
        const json = Buffer.from(query.services, 'base64').toString()
        const serviceData = JSON.parse(json)

        const services = locationStore.getAllServices

        const associations = services.reduce((acc, service) => {
          if (service.associations) {
            acc.push(...service.associations)
          }
          
          return acc
        }, [])

        for (let i = 0; i < serviceData.length; i++) {
          const serviceInfo = serviceData[i]
          const service = services.find((service) => {
            return (service.id == serviceInfo.s)
          })

          if (service) {
            console.log({serviceInfo, service})

            let employeeService = null
            if (serviceInfo.e) {
              employeeService = service.employeeServices.find((employeeService) => {
                return (employeeService.employee.id == serviceInfo.e)
              })
            }

            let employee = null

            if (employeeService && employeeService.employee) {
              employee = employeeService.employee
            }

            let association = null
            if (serviceInfo.a) {
              association = associations.find((association) => {
                return (Number(association.id) == Number(serviceInfo.a))
              })
            }

            console.log({association})

            this.setServiceSelection({ service, employee, association, selected: true, order: (i + 1), disabled: undefined })
          }
        }

        this.disableServices()
      }

      // this.setMode((query.mode) ? query.mode : 'standard')
      // this.setWaitlist((query.waitlist) ? query.waitlist : null)
      // this.setGiftCard((query.giftcard) ? query.giftcard : null)
      this.mode = (query.mode) ? query.mode : 'standard'
      this.waitlist = (query.waitlist) ? query.waitlist : null
      this.giftCard = (query.giftcard) ? query.giftcard : null

      if (!query.customer) {
        this.setSelectedCustomer(null)
      } else if (query.customer && currentQuery.customer !== query.customer) {
        const json = Buffer.from(query.customer, 'base64').toString()
        const customer = JSON.parse(json)

        this.setSelectedCustomer(customer)
      }

      if (!query.date) {
        this.setSelectedDate(null)
      } else if (query.date && currentQuery.date !== query.date) {
        this.setSelectedDate(new Date(parseInt(query.date)))
      }

      this.setFilters({})

      for (const [key, value] of Object.entries(query)) {
        if (key.startsWith('f.')) {
          const filter = key.substring(2)
          this.setFilter({ filter, value })
        }
      }
    }
  },
  getters: {
    selectedServices(state) {
      const selectedServices = []

      const serviceSelections = state.serviceSelections

      for (const id in serviceSelections) {
        if (serviceSelections[id]) {
          const selection = serviceSelections[id]

          if (selection.selected) {
            selectedServices.push(selection)
          }
        }
      }
      selectedServices.sort(function (a, b) {
        return (a.order - b.order)
      })

      return selectedServices
    },
    maxOrder(state) {
      let maxOrder = 0

      const serviceSelections = state.serviceSelections

      for (const id in serviceSelections) {
        if (serviceSelections[id]) {
          const selection = serviceSelections[id]

          if (selection.selected) {
            if (selection.order > maxOrder) {
              maxOrder = selection.order
            }
          }
        }
      }

      return maxOrder
    },
    urlQuery(state) {
      const query = {}
      if (state.serviceSelections) {
        const selectedServices = []
        const serviceSelections = state.serviceSelections

        for (const id in serviceSelections) {
          if (serviceSelections[id]) {
            const selection = serviceSelections[id]

            if (selection.selected) {
              selectedServices.push(selection)
            }
          }
        }

        selectedServices.sort(function (a, b) {
          return (a.order - b.order)
        })

        const serviceData = selectedServices.map((serviceSelection) => {
          const serviceInfo = { s: serviceSelection.service.id }
          if (serviceSelection.selectedEmployee) serviceInfo.e = serviceSelection.selectedEmployee.id
          const associationId = serviceSelection.association?.id ?? serviceSelection.idAssociation
          if (associationId) serviceInfo.a = associationId
          return serviceInfo
        })

        if (serviceData.length > 0) {
          query.services = Buffer.from(JSON.stringify(serviceData)).toString('base64')
        }
      }
      if (state.selectedSlot) {
        query.slot = state.selectedSlot.data
      }
      if (state.selectedDate) {
        query.date = state.selectedDate.getTime()
      }
      if (state.selectedCustomer) {
        query.customer = Buffer.from(JSON.stringify(state.selectedCustomer)).toString('base64')
      }
      if (state.submission) {
        query.submission = Buffer.from(JSON.stringify(state.submission)).toString('base64')
      }
      if (state.rebookReservation) {
        const rebookObject = {
          id: state.rebookReservation.id,
          date: state.rebookReservation.date,
          customer: state.rebookReservation.customer.id,
          signature: state.rebookReservation.signature
        }
        query.rebook = Buffer.from(JSON.stringify(rebookObject)).toString('base64')
      }
      if (state.moveReservation) {
        const moveObject = {
          id: state.moveReservation.id,
          date: state.moveReservation.date,
          customer: state.moveReservation.customer.id,
          signature: state.moveReservation.signature
        }
        query.move = Buffer.from(JSON.stringify(moveObject)).toString('base64')
      }

      if (state.voucher) {
        query.voucher = state.voucher.code
      }

      if (state.pass) {
        const passObject = {
          id: state.pass.id,
          signature: state.pass.signature
        }
        query.pass = Buffer.from(JSON.stringify(passObject)).toString('base64')
      }

      if (state.giftCard) {
        query.giftcard = state.giftCard
      }

      if (state.mode !== 'standard') {
        query.mode = state.mode
      }

      if (state.waitlist) {
        query.waitlist = state.waitlist
      }

      if (state.filters) {
        for (const [key, value] of Object.entries(state.filters)) {
          query['f.' + key] = value
        }
      }

      return query
    }
  }
})
